import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// EU GDPR / ePrivacy cookie consent. Essential storage (sign-in, theme, this choice) is always on;
// analytics and marketing (Google Tag Manager) only run after an explicit opt-in.
// The choice is stored in localStorage and applied on load by public/index.html.
const CONSENT_KEY = 'cookieConsent';
const CONSENT_VERSION = 1; // bump to ask everyone again after the policy changes materially

export const getCookieConsent = () => {
  try {
    const c = JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null');
    return c && c.version === CONSENT_VERSION ? c : null;
  } catch { return null; }
};

// Footer "Cookie settings" links call this to reopen the banner
export const openCookieSettings = () => window.dispatchEvent(new Event('open-cookie-settings'));

// Delete Google Analytics / Ads cookies when consent is withdrawn
const clearTrackingCookies = () => {
  const host = window.location.hostname;
  const domains = ['', host, '.' + host, '.' + host.split('.').slice(-2).join('.')];
  document.cookie.split(';').map(c => c.split('=')[0].trim())
    .filter(name => /^(_ga|_gid|_gat|_gcl|_fbp|_uet)/.test(name))
    .forEach(name => domains.forEach(d => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ''}`;
    }));
};

const applyConsent = ({ analytics, marketing }) => {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: analytics ? 'granted' : 'denied',
      ad_storage: marketing ? 'granted' : 'denied',
      ad_user_data: marketing ? 'granted' : 'denied',
      ad_personalization: marketing ? 'granted' : 'denied'
    });
  }
  if ((analytics || marketing) && typeof window.loadGTM === 'function') window.loadGTM();
  if (!analytics && !marketing) clearTrackingCookies();
};

const Toggle = ({ checked, disabled, onChange, label }) => (
  <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)}
    style={{ width: 42, height: 24, borderRadius: 12, border: 'none', padding: 2, flexShrink: 0, cursor: disabled ? 'not-allowed' : 'pointer',
      background: checked ? '#00d4aa' : '#cbd5e1', opacity: disabled ? 0.6 : 1, transition: 'background 0.2s' }}>
    <span style={{ display: 'block', width: 20, height: 20, borderRadius: '50%', background: '#fff', transform: checked ? 'translateX(18px)' : 'none', transition: 'transform 0.2s' }} />
  </button>
);

const CookieConsent = () => {
  const [open, setOpen] = useState(() => !getCookieConsent());
  const [customize, setCustomize] = useState(false);
  const [choice, setChoice] = useState(() => getCookieConsent() || { analytics: false, marketing: false });

  useEffect(() => {
    const reopen = () => { setChoice(getCookieConsent() || { analytics: false, marketing: false }); setCustomize(true); setOpen(true); };
    window.addEventListener('open-cookie-settings', reopen);
    return () => window.removeEventListener('open-cookie-settings', reopen);
  }, []);

  const save = (selection) => {
    const record = { version: CONSENT_VERSION, analytics: !!selection.analytics, marketing: !!selection.marketing, date: new Date().toISOString() };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
    applyConsent(record);
    setOpen(false);
    setCustomize(false);
  };

  if (!open) return null;

  const btn = { padding: '10px 18px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: 'nowrap' };
  const categories = [
    { key: 'essential', label: 'Essential', desc: 'Keeps you signed in, remembers your theme and this choice. Always on.', locked: true },
    { key: 'analytics', label: 'Analytics', desc: 'Helps us understand how the site is used (Google Analytics via Google Tag Manager).' },
    { key: 'marketing', label: 'Marketing', desc: 'Measures our advertising and shows relevant ads (Google Ads tags).' }
  ];

  return (
    <div role="dialog" aria-live="polite" aria-label="Cookie consent"
      style={{ position: 'fixed', left: 16, right: 16, bottom: 16, zIndex: 10000, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
      <div style={{ pointerEvents: 'auto', width: '100%', maxWidth: 720, background: '#0f172a', color: '#e2e8f0', border: '1px solid #1e293b', borderRadius: 16,
        boxShadow: '0 20px 50px rgba(2,6,23,0.45)', padding: '20px 22px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#f1f5f9', marginBottom: 6 }}>🍪 We value your privacy</div>
        <p style={{ fontSize: 13.5, lineHeight: 1.6, color: '#94a3b8', margin: 0 }}>
          We use essential storage to run the site. With your permission, we'd also like to use analytics and marketing cookies to improve EpiSurveillance.
          You can change your choice anytime from “Cookie settings” in the footer. Read our <Link to="/legal/cookies" style={{ color: '#00d4aa' }}>Cookie Policy</Link>.
        </p>

        {customize && (
          <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
            {categories.map(cat => (
              <div key={cat.key} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 12px', background: '#020617', borderRadius: 10, border: '1px solid #1e293b' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9' }}>{cat.label}</div>
                  <div style={{ fontSize: 12.5, color: '#94a3b8', marginTop: 2 }}>{cat.desc}</div>
                </div>
                <Toggle label={cat.label} checked={cat.locked ? true : !!choice[cat.key]} disabled={cat.locked}
                  onChange={v => setChoice(c => ({ ...c, [cat.key]: v }))} />
              </div>
            ))}
          </div>
        )}

        {/* Reject is as prominent as Accept, as GDPR guidance requires */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          {customize ? (
            <button onClick={() => save(choice)} style={{ ...btn, border: '1px solid #334155', background: 'transparent', color: '#e2e8f0' }}>Save choices</button>
          ) : (
            <button onClick={() => setCustomize(true)} style={{ ...btn, border: 'none', background: 'transparent', color: '#94a3b8', textDecoration: 'underline' }}>Customize</button>
          )}
          <button onClick={() => save({ analytics: false, marketing: false })} style={{ ...btn, border: '1px solid #334155', background: '#1e293b', color: '#f1f5f9' }}>Reject non-essential</button>
          <button onClick={() => save({ analytics: true, marketing: true })} style={{ ...btn, border: 'none', background: '#00d4aa', color: '#020617' }}>Accept all</button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
